'use client';

import React, { createContext, useEffect, useReducer } from 'react';
import { UserInterfaceState } from '@/lib/types/userInterfaceState';
import { emptyUserInterfaceState } from '@/lib/context/userInterfaceDefaults';

import { UserInterfaceReducer } from '@/lib/context/userInterfaceReducer';
import { UserInterfaceActions } from '@/lib/context/userInterfaceActions';

export const UserInterfaceContext = createContext<{
  state: UserInterfaceState;
  dispatch: React.Dispatch<UserInterfaceActions>;
} | null>(null);

export const useUserInterface = () => {
  const user = React.useContext(UserInterfaceContext);
  if (!user) {
    throw new Error('useUserInterface must be used within a UserInterfaceContext.');
  }
  return user;
};

const UserInterfaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const LOCAL_STORAGE_KEY = 'UserInterfaceState';
  const [state, dispatch] = useReducer(UserInterfaceReducer, emptyUserInterfaceState, (initial) => {
    if (typeof window !== 'undefined') {
      const persistedState = localStorage.getItem(LOCAL_STORAGE_KEY);
      return persistedState ? JSON.parse(persistedState) : initial;
    }
    return emptyUserInterfaceState;
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  return (
    <UserInterfaceContext.Provider value={{ state, dispatch }}>
      {children}
    </UserInterfaceContext.Provider>
  );
};

export default UserInterfaceProvider;
